import React, { useState, useMemo } from 'react';
import OperationalTreePanel from './OperationalTreePanel';
import OperationalDetailPanel from './OperationalDetailPanel';
import { UseOperationalDataReturn } from './useOperationalData';
import { cn } from '../../lib/utils';

interface OperationalSplitViewProps {
    data: UseOperationalDataReturn;
}

export default function OperationalSplitView({
    data,
}: OperationalSplitViewProps) {
    const [selectedItemId, setSelectedItemId] = useState<string | null>(null);
    const [searchString, setSearchString] = useState('');
    const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

    const tree = data.state.OperationalOutput.tree;

    const selectedItem = useMemo(() => {
        if (!selectedItemId) return null;
        return tree.items[selectedItemId] || null;
    }, [selectedItemId, tree.items]);

    // Find the corresponding operational input item
    const selectedOperationalItem = useMemo(() => {
        if (!selectedItemId) return null;
        return data.state.OperationalInput.tree.items[selectedItemId] || null;
    }, [selectedItemId, data.state.OperationalInput.tree.items]);

    return (
        <div className="flex h-full min-h-0">
            {/* Left: Tree Panel */}
            <div className={cn(
                "border-r border-slate-200 flex flex-col overflow-hidden bg-white shrink-0 transition-all duration-300",
                sidebarCollapsed ? "w-12" : "w-1/4 min-w-[200px] max-w-[300px]"
            )}>
                {/* Sidebar header with toggle */}
                <div className={cn(
                    "p-3 border-b border-slate-100 flex items-center",
                    sidebarCollapsed ? "justify-center" : "justify-between"
                )}>
                    {!sidebarCollapsed && (
                        <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                            Items
                        </span>
                    )}
                    <button
                        onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
                        className="text-slate-400 hover:text-primary transition-colors"
                        title={sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
                    >
                        <span className="material-symbols-outlined text-sm">
                            {sidebarCollapsed ? 'chevron_right' : 'side_navigation'}
                        </span>
                    </button>
                </div>

                {/* Tree content — hidden when collapsed */}
                {!sidebarCollapsed && (
                    <OperationalTreePanel
                        tree={tree}
                        selectedItemId={selectedItemId}
                        onSelectItem={setSelectedItemId}
                        searchString={searchString}
                        onSearchChange={setSearchString}
                        onDragEnd={data.onDragEndOutput}
                    />
                )}
            </div>

            {/* Right: Detail Panel */}
            <div className="flex-1 overflow-hidden bg-white">
                <OperationalDetailPanel
                    selectedItem={selectedItem}
                    operationalItem={selectedOperationalItem}
                    tree={tree}
                    state={data.state}
                    reloadState={data.reloadState}
                    userRights={data.userRights}
                    onSelectItem={setSelectedItemId}
                />
            </div>
        </div>
    );
}
